/**
 * Normalizes a bundler-produced asset URL into an absolute, same-origin URL.
 *
 * Vite resolves imported static assets to root-relative URLs (e.g. `/src/assets/x.png`
 * in dev, `/static/x.hash.png` in a build). @react-pdf/renderer fetches an <Image src>
 * at PDF-generation time and cannot resolve a root-relative path, which results in blank
 * images in the generated PDF. Prefixing with the current origin makes the URL absolute
 * and fetchable.
 *
 * Values that are already absolute (http/https), data URIs, or blob URLs are returned
 * untouched, so base64 image data (e.g. rendered canvas snapshots) is unaffected.
 *
 * @param {string} url - The asset URL to normalize.
 * @returns {string} An absolute same-origin URL, or the original value when not applicable.
 */
export const toAbsoluteAssetUrl = (url) => {
  if (typeof url !== 'string' || url.length === 0) {
    return url;
  }

  // Already absolute or non-fetchable-by-path (data:, blob:, http:, https:, //).
  if (/^(https?:)?\/\//i.test(url) || /^(data|blob):/i.test(url)) {
    return url;
  }

  if (typeof window === 'undefined' || !window.location || !window.location.origin) {
    return url;
  }

  const origin = window.location.origin;

  if (url.startsWith('/')) {
    return `${origin}${url}`;
  }

  // Relative path without a leading slash - resolve against origin root.
  return `${origin}/${url}`;
};

export default toAbsoluteAssetUrl;
